import { FormOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Slider,
  Space,
  Tag,
  Typography,
  notification,
} from 'antd';
import React, { useCallback, useState } from 'react';
import useProfile from '../../../shared/use-profile';
import { generateUuid } from '../../../shared/utils';
import {
  createTrainerAssessmentMutation,
  trainerAssessmentsForContactQuery,
} from '../constants';

const { Text, Paragraph, Link } = Typography;

// Fixed ITSM module choices (from the AIMS "Trainer Assessment" form).
const FOUNDATIONS_MODULES = [
  'Module 1: The Master Plan',
  'Module 2: The Mission',
  'Module 3: State of the World',
  'Module 4: Personalizing the Great Commission',
  'Module 5: How to Adopt a UPG',
];
const TOOLS_MODULES = [
  'Module 6: Church Planting Movements',
  'Module 7: Networking Through Partnerships',
  'Module 8: Goers and Senders',
  'Module 9: Faith Promise Giving',
];

const SCORE_MARKS = { 1: '1', 2: '2', 3: '3', 4: '4', 5: '5' };

// Derived grade from the three 1-5 scores (display only — not stored).
function gradeFor(k, q, m) {
  if (!k || !q || !m) return null;
  const avg = (k + q + m) / 3;
  if (avg >= 4.5) return { label: 'Excellent', color: 'green' };
  if (avg >= 3.5) return { label: 'Good', color: 'blue' };
  if (avg >= 2.5) return { label: 'Fair', color: 'gold' };
  return { label: 'Poor', color: 'orange' };
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString() : '—';
}

// Renders on the trainer-assessment (TRAINER_ASSESSMENT_PASSED) onboarding task: a
// "Fill out assessment" button that records a trainer-assessment for the contact,
// recreating the AIMS Trainer Assessment form, plus links to view any assessments
// already recorded. The step auto-completes only when a certified (passed) assessment
// exists — a not-certified attempt leaves it open for a reassessment.
function TrainerAssessmentAction({ contactId, contactName, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [form] = Form.useForm();
  const profile = useProfile();
  const [createAssessment, { loading }] = useMutation(
    createTrainerAssessmentMutation,
  );
  const { data, refetch } = useQuery(trainerAssessmentsForContactQuery, {
    variables: { trainerPartnerId: contactId },
    skip: !contactId,
    fetchPolicy: 'cache-and-network',
  });
  const assessments = data?.allTrainerAssessments || [];

  const knowledgeScore = Form.useWatch('knowledgeScore', form);
  const questionsScore = Form.useWatch('questionsScore', form);
  const mobilizeScore = Form.useWatch('mobilizeScore', form);
  const certified = Form.useWatch('certified', form);
  const grade = gradeFor(knowledgeScore, questionsScore, mobilizeScore);

  const handleFinish = useCallback(
    async (values) => {
      try {
        await createAssessment({
          variables: {
            assessment: {
              _id: generateUuid(),
              trainerPartnerId: contactId,
              assessorEmail: profile?.email || '',
              timestamp: new Date().toISOString(),
              itsmFoundationsModules: values.itsmFoundationsModules || [],
              itsmToolsModules: values.itsmToolsModules || [],
              knowledgeScore: values.knowledgeScore,
              questionsScore: values.questionsScore,
              mobilizeScore: values.mobilizeScore,
              improvementNotes: values.improvementNotes,
              certified: !!values.certified,
              // Only captured when the trainer is not certified (a reassessment is scheduled).
              reassessmentDate:
                !values.certified && values.reassessmentDate
                  ? values.reassessmentDate.toISOString()
                  : undefined,
            },
          },
        });
        notification.success({ message: 'Assessment recorded' });
        setOpen(false);
        form.resetFields();
        await refetch();
        if (onSubmitted) await onSubmitted();
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
    },
    [createAssessment, contactId, profile, form, refetch, onSubmitted],
  );

  return (
    <>
      <Button
        size="small"
        icon={<FormOutlined />}
        onClick={() => setOpen(true)}
        style={{ marginLeft: 8 }}
        disabled={!contactId}
      >
        Fill out assessment
      </Button>
      {assessments.length > 0 && (
        <Space size={4} wrap style={{ marginLeft: 8 }}>
          <Text type="secondary">Recorded:</Text>
          {assessments.map((a) => (
            <Link key={a._id} onClick={() => setViewing(a)}>
              {formatDate(a.timestamp)}{' '}
              <Tag
                color={a.certified ? 'green' : 'gold'}
                style={{ marginInlineEnd: 0 }}
              >
                {a.certified ? 'Certified' : 'Not certified'}
              </Tag>
            </Link>
          ))}
        </Space>
      )}

      <Modal
        open={open}
        title="Trainer Assessment"
        okText="Record assessment"
        okButtonProps={{ loading }}
        onOk={() => form.submit()}
        onCancel={() => setOpen(false)}
        width={600}
        destroyOnClose
      >
        <Paragraph type="secondary">
          Fill this out while the potential trainer or coordinator is teaching
          you or others ITSM.
        </Paragraph>
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item label="Who is training?">
            <Input value={contactName || contactId} disabled />
          </Form.Item>

          <Form.Item
            name="itsmFoundationsModules"
            label="What ITSM Foundations Module did the trainer teach?"
            rules={[{ required: true, message: 'Select at least one module' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select module(s)"
              options={FOUNDATIONS_MODULES.map((m) => ({ value: m, label: m }))}
            />
          </Form.Item>
          <Form.Item
            name="itsmToolsModules"
            label="What ITSM Tools Module did the trainer teach?"
            rules={[{ required: true, message: 'Select at least one module' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select module(s)"
              options={TOOLS_MODULES.map((m) => ({ value: m, label: m }))}
            />
          </Form.Item>

          <Form.Item
            name="knowledgeScore"
            label="Did the trainer demonstrate knowledge of the module?"
            extra="1 - Lack of Understanding; 5 - Very Knowledgeable"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Slider min={1} max={5} marks={SCORE_MARKS} />
          </Form.Item>
          <Form.Item
            name="questionsScore"
            label="Did the trainer answer questions from the attendees well?"
            extra="1 - Lack of Answers; 5 - Clear Answers"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Slider min={1} max={5} marks={SCORE_MARKS} />
          </Form.Item>
          <Form.Item
            name="mobilizeScore"
            label="Will the trainer be able to mobilize strong partnerships?"
            extra="1 - Produce Weak Partnerships; 5 - Strong Partnerships"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Slider min={1} max={5} marks={SCORE_MARKS} />
          </Form.Item>

          <Form.Item
            name="improvementNotes"
            label="What can the trainer improve on?"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item label="Grade">
            {grade ? (
              <Tag color={grade.color}>{grade.label}</Tag>
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Form.Item>

          <Form.Item
            name="certified"
            label="Do you certify the trainer to train in the AIMS network?"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Radio.Group optionType="button">
              <Radio.Button value={false}>✗ No</Radio.Button>
              <Radio.Button value>✓ I Certify the Trainer</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {certified === false && (
            <Form.Item
              name="reassessmentDate"
              label="When will you reassess the trainer?"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        open={!!viewing}
        title="Trainer Assessment"
        footer={null}
        onCancel={() => setViewing(null)}
        width={600}
      >
        {viewing && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Trainer">
              {contactName || viewing.trainerPartnerId}
            </Descriptions.Item>
            <Descriptions.Item label="Assessor">
              {viewing.assessorEmail || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Date">
              {formatDate(viewing.timestamp)}
            </Descriptions.Item>
            <Descriptions.Item label="ITSM Foundations">
              {(viewing.itsmFoundationsModules || []).join(', ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="ITSM Tools">
              {(viewing.itsmToolsModules || []).join(', ') || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Knowledge">
              {viewing.knowledgeScore}/5
            </Descriptions.Item>
            <Descriptions.Item label="Questions">
              {viewing.questionsScore}/5
            </Descriptions.Item>
            <Descriptions.Item label="Mobilize">
              {viewing.mobilizeScore}/5
            </Descriptions.Item>
            <Descriptions.Item label="Improvement notes">
              {viewing.improvementNotes || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Certified">
              <Tag color={viewing.certified ? 'green' : 'gold'}>
                {viewing.certified ? 'Certified' : 'Not certified'}
              </Tag>
            </Descriptions.Item>
            {!viewing.certified && (
              <Descriptions.Item label="Reassessment date">
                {formatDate(viewing.reassessmentDate)}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </>
  );
}

export default TrainerAssessmentAction;
